/**
 * Data prefetching for likely next screens.
 *
 * Router `preload` already warms a route's code chunk; this warms the *data*
 * behind it, using the exact same query keys the destination screen reads
 * from (see `screen-queries.ts`). By the time the user taps, the screen
 * renders straight from cache.
 *
 * Read-only, best-effort and idle-scheduled: failures are ignored, nothing is
 * written, and RLS still governs what comes back.
 */
import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import {
  incidentsQuery,
  scheduleQuery,
  sitesQuery,
  teamQuery,
  timeClockQuery,
} from "@/lib/screen-queries";

type Ctx = { orgId: string; userId: string };

/** Route path -> data warm-up for that screen. */
const REGISTRY: Record<string, (qc: QueryClient, ctx: Ctx) => Promise<unknown>> = {
  "/sites": (qc, { orgId }) => qc.prefetchQuery(sitesQuery(orgId)),
  "/incidents": (qc, { orgId }) => qc.prefetchQuery(incidentsQuery(orgId)),
  "/schedule": (qc, { orgId }) => qc.prefetchQuery(scheduleQuery(orgId)),
  "/team": (qc, { orgId, userId }) => qc.prefetchQuery(teamQuery(orgId, userId)),
  "/time-clock": (qc, { orgId, userId }) => qc.prefetchQuery(timeClockQuery(orgId, userId)),
};

export function prefetchScreen(qc: QueryClient, route: string, ctx: Ctx) {
  const fn = REGISTRY[route];
  if (!fn || !ctx.orgId) return;
  void fn(qc, ctx).catch(() => {
    /* best-effort: the screen will fetch normally on arrival */
  });
}

/** Warm the given routes once the browser is idle. */
export function usePrefetchScreens(routes: string[]) {
  const qc = useQueryClient();
  const { currentOrg, user } = useAuth();
  const orgId = currentOrg?.id ?? "";
  const userId = user?.id ?? "";
  const key = routes.join("|");

  useEffect(() => {
    if (!orgId) return;
    const list = key ? key.split("|") : [];
    const run = () => list.forEach((r) => prefetchScreen(qc, r, { orgId, userId }));
    const idle = (globalThis as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout?: number }) => number)
      | undefined;
    if (idle) {
      const handle = idle(run, { timeout: 1500 });
      return () => (globalThis as any).cancelIdleCallback?.(handle);
    }
    const t = setTimeout(run, 400);
    return () => clearTimeout(t);
  }, [qc, orgId, userId, key]);
}

/**
 * Handlers for links that aren't pinned: warm the data the moment the user
 * shows intent (hover / touch / keyboard focus).
 */
export function usePrefetchOnIntent() {
  const qc = useQueryClient();
  const { currentOrg, user } = useAuth();
  const orgId = currentOrg?.id ?? "";
  const userId = user?.id ?? "";
  return (route: string) => ({
    onPointerEnter: () => prefetchScreen(qc, route, { orgId, userId }),
    onTouchStart: () => prefetchScreen(qc, route, { orgId, userId }),
    onFocus: () => prefetchScreen(qc, route, { orgId, userId }),
  });
}
